import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle2,
  Music2,
  Sparkles,
  Heart,
  ShieldCheck,
  Clock,
  Gift,
  Star,
  ArrowRight,
} from "lucide-react";
import musikonzeptLogo from "@/assets/musikonzept-logo.png";
import teamPhoto from "@/assets/fachkraft-musik-team.jpg";
// Echte Kurs-Thumbnails aus dem Supabase Storage (course-thumbnails Bucket)
const photoKreativpaket =
  "https://vqcngohkfhvdrmnysyck.supabase.co/storage/v1/object/public/course-files/course-thumbnails/kreativpaket-bruno-baer.png";
const photoWebinar =
  "https://vqcngohkfhvdrmnysyck.supabase.co/storage/v1/object/public/course-files/course-thumbnails/webinar-aufzeichnung-klangvolle-kita-feste-musikideen-fuer-besondere-anlaesse.jpg";
const photoFortbildung =
  "https://vqcngohkfhvdrmnysyck.supabase.co/storage/v1/object/public/course-files/course-thumbnails/boomwhackers-leicht-lernen-spiel-deinen-beat.jpg";
import awardBadge from "@/assets/award-studienpreis-2025.png";
import { Users, Award, MapPin, BookOpen, Video, Package } from "lucide-react";
import { buildRegisterHref } from "@/lib/kostenlos/buildRegisterHref";
import { trackViewContent, trackLead } from "@/lib/marketing/pixel";
import { FACHKRAEFTE_KURZ, FACHKRAEFTE_TEXT, gross } from "@/config/webseiteFakten";

const Kostenlos = () => {
  const registerHref = useMemo(() => buildRegisterHref(), []);

  useEffect(() => {
    try {
      trackViewContent({
        content_name: "Kostenlos LP",
        content_category: "kostenlos",
      });
    } catch {
      /* noop */
    }
  }, []);

  const handleCtaClick = (cta: string) => {
    try {
      trackLead({ cta_location: cta, content_name: "Kostenlos LP" });
    } catch {
      /* noop */
    }
  };

  const benefits = [
    {
      icon: Package,
      badge: "Kreativpaket",
      title: "Bruno Bär – das Kreativpaket",
      text: "Komplettes Mitmach-Paket rund um Bruno Bär: Lieder, Klanggeschichte, Bewegungsspiele und Bastelideen – sofort einsetzbar im Morgenkreis.",
      photo: photoKreativpaket,
      gradient: "from-primary/30 via-primary/5 to-transparent",
      ring: "ring-primary/30",
      iconBg: "bg-primary text-primary-foreground",
    },
    {
      icon: Video,
      badge: "Webinar-Aufzeichnung",
      title: "Kita-Feste musikalisch gestalten",
      text: "Komplette Webinar-Aufzeichnung mit frischen Ideen für Sommerfest, Laternenfest & Co. – Schritt für Schritt erklärt.",
      photo: photoWebinar,
      gradient: "from-accent/30 via-accent/5 to-transparent",
      ring: "ring-accent/40",
      iconBg: "bg-accent text-accent-foreground",
    },
    {
      icon: BookOpen,
      badge: "Im Wert von 89 €",
      title: "Eine Online-Fortbildung deiner Wahl",
      text: "Such dir eine zertifizierte Online-Fortbildung aus unserem Programm aus – im Wert von 89 €, geschenkt für dich.",
      photo: photoFortbildung,
      gradient: "from-primary/25 via-accent/10 to-transparent",
      ring: "ring-primary/30",
      iconBg: "bg-gradient-to-br from-primary to-accent text-white",
    },
  ];

  const steps = [
    {
      n: 1,
      title: "Kostenlos registrieren",
      text: "In unter 60 Sekunden mit E-Mail-Adresse anmelden – ohne Kreditkarte.",
      color: "bg-primary",
      soft: "bg-primary/10",
      border: "border-primary/30",
    },
    {
      n: 2,
      title: "Lieder & Ideen entdecken",
      text: "Sofortiger Zugriff auf deinen Start-Bereich mit kuratierten Inhalten für die Kita.",
      color: "bg-accent",
      soft: "bg-accent/10",
      border: "border-accent/40",
    },
    {
      n: 3,
      title: "Im Alltag einsetzen",
      text: "Singen, spielen, Kinderaugen leuchten lassen – und jederzeit erweitern.",
      color: "bg-gradient-to-br from-primary to-accent",
      soft: "bg-gradient-to-br from-primary/10 to-accent/10",
      border: "border-primary/30",
    },
  ];

  const trustStats = [
    {
      icon: Users,
      value: FACHKRAEFTE_KURZ,
      label: "Fachkräfte lernen mit MusiKonzept",
    },
    {
      icon: MapPin,
      value: "Tausende Kitas",
      label: "in DACH nutzen unsere Lieder",
    },
    {
      icon: Award,
      value: "Größte Plattform",
      label: "für Musik in der Kita im deutschsprachigen Raum",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Kostenlos starten – Mitmach-Lieder für die Kita | MusiKonzept</title>
        <meta
          name="description"
          content="Über 50 kostenlose Mitmach-Lieder und Praxis-Ideen für Erzieherinnen. In 60 Sekunden registrieren – ohne Kreditkarte, ohne Risiko."
        />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      {/* Sticky-leichte Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="container max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <img src={musikonzeptLogo} alt="MusiKonzept" className="h-9 w-auto" />
          </Link>
          <Link to={registerHref} onClick={() => handleCtaClick("header")}>
            <Button size="sm" className="font-medium">
              Kostenlos starten
            </Button>
          </Link>
        </div>
      </header>

      {/* 1) HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/15 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
        <div className="relative container max-w-6xl mx-auto px-4 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
                <Gift className="h-3.5 w-3.5" />
                100 % kostenlos · keine Kreditkarte
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight text-foreground">
                Über 50 Mitmach-Lieder für die Kita –{" "}
                <span className="text-primary">kostenlos</span> für dich
              </h1>
              <p className="mt-4 text-base md:text-lg text-muted-foreground">
                Hol dir sofort frische Lieder, Klanggeschichten und Bewegungs-Ideen,
                die Kinder lieben. Kuratiert von Erzieherinnen, sofort einsetzbar im
                Morgenkreis.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link to={registerHref} onClick={() => handleCtaClick("hero")} className="block">
                  <Button size="lg" className="w-full sm:w-auto text-base font-semibold">
                    Jetzt kostenlos starten
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Kein Abo · jederzeit nutzbar
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-4 w-4 text-primary" />
                  In 60 Sekunden startklar
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-primary/15 rounded-3xl blur-2xl" />
              <img
                src={teamPhoto}
                alt="Das MusiKonzept-Team"
                className="relative rounded-2xl shadow-2xl object-cover w-full aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST-SIEGEL */}
      <section className="border-y border-primary/15 bg-gradient-to-r from-primary/5 via-background to-accent/10">
        <div className="container max-w-6xl mx-auto px-4 py-8">
          <div className="grid sm:grid-cols-3 gap-4">
            {trustStats.map((t) => (
              <div
                key={t.label}
                className="flex items-center gap-3 rounded-xl bg-background/70 backdrop-blur border border-primary/20 px-4 py-3 shadow-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white shrink-0">
                  <t.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-foreground leading-tight">{t.value}</div>
                  <div className="text-xs text-muted-foreground leading-snug">{t.label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 opacity-90">
            <img src={musikonzeptLogo} alt="MusiKonzept Logo" className="h-8 w-auto" />
            <img src={awardBadge} alt="Studienpreis 2025" className="h-12 w-auto" />
          </div>
        </div>
      </section>

      {/* 2) VORTEILE */}
      <section className="relative overflow-hidden py-12 md:py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute -top-10 -left-20 w-80 h-80 bg-accent/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -right-20 w-80 h-80 bg-primary/15 rounded-full blur-3xl" />
        <div className="relative container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent-foreground/80 mb-3">
              <Gift className="h-3.5 w-3.5 text-accent" />
              Dein Sofort-Paket
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Was du sofort bekommst
            </h2>
            <p className="mt-3 text-muted-foreground">
              Kein Download-Chaos, keine zerstückelten YouTube-Videos. Alles an einem
              Ort, auf dich als Erzieherin zugeschnitten.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {benefits.map((b) => (
              <Card
                key={b.title}
                className={`overflow-hidden border-0 ring-1 ${b.ring} shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
              >
                <div className="relative h-44 sm:h-40 overflow-hidden">
                  <img
                    src={b.photo}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${b.gradient}`} />
                  <div className={`absolute top-3 left-3 w-11 h-11 rounded-xl ${b.iconBg} flex items-center justify-center shadow-lg`}>
                    <b.icon className="h-5 w-5" />
                  </div>
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-background/90 backdrop-blur text-[10px] font-bold uppercase tracking-wide text-foreground shadow">
                    {b.badge}
                  </div>
                </div>
                <CardContent className="pt-5 bg-background">
                  <h3 className="font-semibold text-foreground leading-snug">{b.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{b.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* 3) 3-SCHRITTE */}
      <section className="relative overflow-hidden py-12 md:py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10" />
        <div className="relative container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary mb-3">
              <Clock className="h-3.5 w-3.5" />
              In unter 60 Sekunden
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              In 3 Schritten startklar
            </h2>
            <p className="mt-3 text-muted-foreground">
              So einfach kommst du an deine kostenlosen Mitmach-Lieder.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 relative pt-6">
            {steps.map((s, i) => (
              <div
                key={s.n}
                className={`relative bg-background rounded-2xl p-5 md:p-6 border-2 ${s.border} shadow-md hover:shadow-xl transition-shadow`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-full ${s.color} text-white flex items-center justify-center font-bold text-lg shadow-md shrink-0`}>
                    {s.n}
                  </div>
                  <div className={`px-2 py-0.5 rounded-full ${s.soft} text-[10px] font-bold uppercase tracking-wide text-foreground/70`}>
                    Schritt {s.n}
                  </div>
                </div>
                <h3 className="font-bold text-lg text-foreground">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{s.text}</p>
                {i < steps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-5 h-6 w-6 text-primary/40 -translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to={registerHref} onClick={() => handleCtaClick("steps")} className="inline-block">
              <Button size="lg" className="text-base font-semibold shadow-lg">
                Jetzt Schritt 1 starten
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4) CHECKLISTE */}
      <section className="container max-w-4xl mx-auto px-4 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">
          Für dich enthalten
        </h2>
        <ul className="mt-8 grid sm:grid-cols-2 gap-3">
          {[
            "Über 50 Mitmach-Lieder mit Audio & Video",
            "Klanggeschichten für Krippe & Kita",
            "Bewegungs-Spiele für drinnen & draußen",
            "Praktische Anleitungen – kein Notenstudium nötig",
            "Frische Ideen direkt aus der Praxis",
            "Zugriff auf Handy, Tablet und Laptop",
          ].map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 text-sm text-foreground bg-background rounded-lg border border-border/60 p-3"
            >
              <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* 5) STIMMEN */}
      <section className="bg-muted/30 py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">
            Das sagen Erzieherinnen
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {[
              {
                name: "Sandra K.",
                role: "Erzieherin, Kita Sonnenschein",
                text: "Endlich Lieder, die wirklich funktionieren. Die Kinder sind sofort dabei – und ich auch.",
              },
              {
                name: "Melanie B.",
                role: "Gruppenleitung Krippe",
                text: "Klar erklärt, sofort einsetzbar. Spart mir jede Woche stundenlanges Suchen auf YouTube.",
              },
            ].map((t) => (
              <Card key={t.name} className="border-border/60">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground">„{t.text}"</p>
                  <p className="mt-4 text-xs text-muted-foreground font-medium">
                    {t.name} · {t.role}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 6) FAQ */}
      <section className="container max-w-3xl mx-auto px-4 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">
          Häufige Fragen
        </h2>
        <div className="mt-8 space-y-4">
          {[
            {
              q: "Ist das wirklich kostenlos?",
              a: "Ja. Du erstellst kostenlos einen Account und erhältst direkt Zugriff auf den Start-Bereich – ohne Kreditkarte, ohne automatische Verlängerung.",
            },
            {
              q: "Muss ich Noten lesen können?",
              a: "Nein. Alle Lieder sind in einfachen Mitmach-Versionen mit Video und Audio enthalten – ohne musikalische Vorkenntnisse einsetzbar.",
            },
            {
              q: "Wie lange habe ich Zugriff?",
              a: "So lange du willst. Dein kostenloser Account bleibt bestehen – du kannst jederzeit erweitern, musst aber nicht.",
            },
          ].map((f) => (
            <details key={f.q} className="rounded-xl border border-border/60 bg-background p-4 group">
              <summary className="font-medium text-foreground cursor-pointer list-none flex items-center justify-between">
                {f.q}
                <span className="text-primary group-open:rotate-90 transition-transform">›</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* 7) VERTRAUEN */}
      <section className="bg-muted/30 py-10">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              DSGVO-konform · Server in der EU
            </span>
            <span className="inline-flex items-center gap-2">
              <Heart className="h-4 w-4 text-primary" />
              {gross(FACHKRAEFTE_TEXT)} vertrauen uns
            </span>
          </div>
        </div>
      </section>

      {/* 8) FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/15 via-background to-accent/15" />
        <div className="relative container max-w-3xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Bring frische Lieder in deine Kita
          </h2>
          <p className="mt-3 text-muted-foreground">
            Kostenlos, sofort einsatzbereit, ohne Risiko.
          </p>
          <Link to={registerHref} onClick={() => handleCtaClick("final")} className="inline-block mt-6">
            <Button size="lg" className="text-base font-semibold">
              Jetzt kostenlos starten
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-muted-foreground">
            Kein Abo · keine Kreditkarte · in 60 Sekunden startklar
          </p>
        </div>
      </section>

      {/* 9) FOOTER */}
      <footer className="border-t border-border/50 py-8">
        <div className="container max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} MusiKonzept</div>
          <div className="flex gap-4">
            <Link to="/impressum" className="hover:text-foreground">
              Impressum
            </Link>
            <Link to="/datenschutz" className="hover:text-foreground">
              Datenschutz
            </Link>
            <Link to="/agb" className="hover:text-foreground">
              AGB
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Kostenlos;
